class CartDrawer extends HTMLElement {
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();
  }

  /* Overlay/backdrop must bind here — constructor runs before children exist on some browsers */
  connectedCallback() {
    if (this._cartDrawerUiBound) return;
    this._cartDrawerUiBound = true;

    if (this.hasAttribute('data-express-checkout')) {
      this._express = new CartExpressCheckout(this);
    }

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this._express?.handleEscape()) return;
      this.close();
    });
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.addEventListener('click', (event) => {
      if (this._express?.handleClick(event)) return;
      if (this.classList.contains('active') && !event.target.closest('.drawer__inner')) {
        this.close();
      }
    });

    /*
      The shipping bar's text/fill are the ONLY pieces of the cart drawer
      kept in sync purely via JS (the bar's DOM node itself is never
      replaced). This is the single subscribe handler that owns those
      updates — every cart-mutation flow (cart-items change/remove,
      product-form add, quick-add, melting-surcharge, subscription-landing)
      publishes PUB_SUB_EVENTS.cartUpdate, and we route through one
      computation backed by the persisted data-fs-* attrs on this wrapper.
    */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this._refreshShippingBar(event && event.cartData);
      });
    }

    /*
      Self-heal the initial server render. The bar can ship with stale free-
      shipping settings if the published theme settings haven't synced with
      the current overlay-group config — recompute once on connect so the
      bar reflects the live cart, not the build-time defaults.
    */
    this._refreshShippingBar();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = undefined;
    }
    this._express?.destroy();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    if (window.Alpine?.store('overlay')?.open) {
      window.Alpine.store('overlay').hide();
    }

    const megaMenu = document.querySelector('mega-menu-drawer');
    if (megaMenu?.querySelector('#MegaMenuDrawer')?.open) {
      megaMenu.close();
    }

    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification) cartNotification.close();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    if (this._openActiveTimeout != null) {
      clearTimeout(this._openActiveTimeout);
      this._openActiveTimeout = null;
    }
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this._openActiveTimeout = setTimeout(() => {
      this._openActiveTimeout = null;
      this.classList.add('animate', 'active');
    }, 0);

    this.addEventListener(
      'transitionend',
      () => {
        if (this._express?.isOpen) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (this._openActiveTimeout != null) {
      clearTimeout(this._openActiveTimeout);
      this._openActiveTimeout = null;
    }
    this._express?.close();
    this.classList.remove('active', 'animate');
    const returnFocus = this.activeElement;
    /* Tear down trap without focusing synchronously — refocus during the header
       cart control’s own click can re-fire activation (X inside drawer is fine). */
    removeTrapFocus(null);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');

    requestAnimationFrame(() => {
      if (returnFocus) {
        if (typeof returnFocus.focus === 'function') {
          try {
            returnFocus.focus({ preventScroll: true });
          } catch {
            returnFocus.focus();
          }
        }
      }
      void this.offsetWidth;
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    /*
      Sync the `is-empty` class on the <cart-drawer> wrapper BEFORE swapping
      inner regions. The wrapper itself is never replaced, so its classes
      persist between renders. Source of truth: the `is-empty` class on the
      <cart-drawer> element in the section HTML we're about to render — more
      robust than e.g. `item_count` (not present on /cart/add.js responses).
    */
    const cartDrawerSectionHtml = parsedState?.sections?.['cart-drawer'];
    if (cartDrawerSectionHtml) {
      const sourceWrapper = new DOMParser()
        .parseFromString(cartDrawerSectionHtml, 'text/html')
        .querySelector('cart-drawer');
      if (sourceWrapper) {
        this.classList.toggle('is-empty', sourceWrapper.classList.contains('is-empty'));
      }
    }
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    /*
      Selective swap (mirrors cart.js → patchCartDrawerNonEmptyFromSectionHtml):
      only the three inner regions that depend on cart state are replaced.
      Everything else — the <cart-drawer> wrapper (with data-fs-* attrs),
      .drawer__inner, .drawer__inner-empty, the .cart-drawer__shipping-bar
      sibling above .cart-drawer__footer — is preserved. This is what keeps
      the shipping bar safe from stale Section Rendering API output: its DOM
      node simply isn't part of the swap.
    */
    if (cartDrawerSectionHtml) {
      const sourceDoc = new DOMParser().parseFromString(cartDrawerSectionHtml, 'text/html');
      const pairs = [
        ['.drawer__header', () => this.querySelector('.drawer__header')],
        ['cart-drawer-items', () => this.querySelector('cart-drawer-items')],
        ['.cart-drawer__footer', () => this.querySelector('.cart-drawer__footer')],
      ];
      for (const [sel, getTarget] of pairs) {
        const src = sourceDoc.querySelector(sel);
        const tgt = getTarget();
        if (src && tgt) tgt.replaceWith(src);
      }
    }

    /*
      cart-icon-bubble is rendered separately and lives in the header — keep
      the original full-innerHTML swap for it.
    */
    const bubbleHtml = parsedState?.sections?.['cart-icon-bubble'];
    const bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubbleEl) {
      bubbleEl.innerHTML = this.getSectionInnerHTML(bubbleHtml);
    }

    this._refreshShippingBar();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  async _refreshShippingBar(cartState) {
    if (this.dataset.fsEnabled !== 'true') return;

    /*
      Bar markup lives outside .cart-drawer__footer and is therefore never
      replaced by AJAX swaps (neither cart.js's patchCartDrawerNonEmpty…,
      nor this component's renderContents, touch it). So a query for the bar
      is stable and `data-*` hooks rendered server-side on initial page load
      remain authoritative.
    */
    const bar = this.querySelector('[data-cart-shipping-bar]');
    if (!bar) return;

    const parsedThresholdCents = parseInt(this.dataset.fsThresholdCents, 10);
    const thresholdCents = parsedThresholdCents > 0 ? parsedThresholdCents : 0;
    const thresholdQualifies = this.dataset.fsThresholdQualifies === 'true';
    const subscriptionQualifies = this.dataset.fsSubscriptionQualifies === 'true';
    const progressText = this.dataset.fsProgressText || '';
    const qualifiedText = this.dataset.fsQualifiedText || '';

    /*
      `/cart/add.js` returns just the added line (no `total_price`). Fetch
      `/cart.js` whenever the payload doesn't carry usable cart totals.
    */
    let state = cartState;
    if (!state || typeof state.total_price !== 'number' || !Array.isArray(state.items)) {
      try {
        state = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((r) => r.json());
      } catch {
        return;
      }
    }

    const totalPrice = Number(state.total_price) || 0;
    const hasSubscription = (state.items || []).some(
      (it) => it && it.selling_plan_allocation != null
    );

    const qualifiesByThreshold = thresholdQualifies && thresholdCents > 0 && totalPrice >= thresholdCents;
    const qualifiesBySubscription = subscriptionQualifies && hasSubscription;
    const isQualified = qualifiesByThreshold || qualifiesBySubscription;

    const progressPct = isQualified
      ? 100
      : thresholdCents > 0
        ? Math.min(100, Math.floor((totalPrice * 100) / thresholdCents))
        : 0;

    const textEl = bar.querySelector('[data-fs-text]');
    if (textEl) {
      if (isQualified) {
        textEl.innerHTML = qualifiedText;
      } else {
        const remaining = Math.max(0, thresholdCents - totalPrice);
        textEl.innerHTML = progressText.replace('[remaining]', this._formatMoney(remaining));
      }
    }

    const fillEl = bar.querySelector('[data-fs-fill]');
    if (fillEl) fillEl.style.width = `${progressPct}%`;

    this._syncShippingAmount(isQualified);
  }

  _syncShippingAmount(isQualified) {
    const cost = this.dataset.cartShippingCost;
    if (!cost) return;
    const amount = this.querySelector('[data-cart-shipping-amount]');
    if (!amount) return;
    amount.textContent = isQualified ? this.dataset.cartShippingFreeAmount || cost : cost;
  }

  _formatMoney(cents) {
    const code = (typeof window !== 'undefined' && window.Shopify?.currency?.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
